import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  CloudQueue as ServicesIcon,
  People as UsersIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  AccountCircle,
  LockOpen as LockOpenIcon,
  Category as CategoryIcon,
  Quiz as QuizIcon,
  OpenInNew as OpenInNewIcon,
  Analytics as AnalyticsIcon,
  Storage as StorageIcon,
  Hotel as HotelIcon,
  SmartToy as ConciergeIcon,
  Api as ApiIcon,
  Security as SecurityIcon,
  AccountTree as PipelineIcon,
  Article as ContentIcon,
  RocketLaunch as RocketLaunchIcon,
  FactCheck as FactCheckIcon,
  MenuBook as GuidesIcon,
  ToggleOn as FeatureFlagsIcon,
  Badge as StaffIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/GoogleAuthContext';
import RequestAccessDialog from './RequestAccessDialog';

const drawerWidth = 280;

// Determine environment from hostname
const isProduction = () => {
  const hostname = window.location.hostname;
  return !hostname.includes('internal') && !hostname.includes('staging') && !hostname.includes('localhost');
};

// Quick Links - environment-aware external tools
const getQuickLinks = () => {
  const isProd = isProduction();
  return [
    {
      text: 'Langfuse',
      icon: <AnalyticsIcon />,
      url: isProd ? 'https://llm-observability.api.tripsby.ai/' : 'https://langfuse.api.dev.tripsby.ai/',
      description: 'LLM Observability',
    },
    {
      text: 'Elastic Logs',
      icon: <StorageIcon />,
      url: isProd ? 'http://logs.api.tripsby.ai:5601' : 'http://logs.api.dev.tripsby.ai:5601/login',
      description: 'Application Logs',
    },
    {
      text: 'VervoTech',
      icon: <HotelIcon />,
      url: 'https://clients.vervotech.com/',
      description: 'Hotel Mapping Provider',
    },
    {
      text: 'ZentrumHub',
      icon: <HotelIcon />,
      url: 'https://backoffice-nexus.prod.zentrumhub.com/',
      description: 'Hotel Content Hub',
    },
    {
      text: 'WebBeds',
      icon: <HotelIcon />,
      url: 'http://us.dotwconnect.com/interface/en/login',
      description: 'DOTW Booking Site',
    },
    {
      text: 'Hotel Beds',
      icon: <HotelIcon />,
      url: 'https://developer.hotelbeds.com/login/',
      description: 'APItude Developer Portal',
    },
  ];
};

interface DashboardLayoutProps {
  children: React.ReactNode;
}


/**
 * Sidebar highlight. Most items match their path exactly, but two own a whole
 * subtree: the Editorial CMS sub-router (`/content/cms/*`) and the guide detail
 * screen (`/guides/:slug`). Exact match left the CMS item unhighlighted on every
 * page inside it.
 */
function isNavItemSelected(pathname: string, itemPath: string): boolean {
  if (itemPath === '/content/cms' || itemPath === '/guides') {
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
  }
  return pathname === itemPath;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [requestAccessOpen, setRequestAccessOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // Menu items with permission-based visibility
  // Each item now uses the fine-grained permission system
  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
      permissionTab: 'dashboard' as const,
    },
    {
      text: 'Services',
      icon: <ServicesIcon />,
      path: '/services',
      permissionTab: 'services' as const,
    },
    {
      text: 'Permissions',
      icon: <ServicesIcon />,
      path: '/permissions',
      permissionTab: 'permissions' as const,
    },
    {
      text: 'Service Discovery',
      icon: <ServicesIcon />,
      path: '/service-discovery',
      permissionTab: 'service_discovery' as const,
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      permissionTab: 'settings' as const,
    },
    {
      text: 'Background Search',
      icon: <SearchIcon />,
      path: '/background-search',
      permissionTab: 'settings' as const,
    },
    {
      text: 'Audit Log',
      icon: <HistoryIcon />,
      path: '/audit-log',
      permissionTab: 'audit_log' as const,
    },
    {
      text: 'Match Review',
      icon: <FactCheckIcon />,
      path: '/match-review',
      permissionTab: 'audit_log' as const, // reuse audit_log gate (ops review surface)
    },
    {
      text: 'Generic Terms',
      icon: <CategoryIcon />,
      path: '/generic-terms',
      permissionTab: 'generic_terms' as const,
    },
    {
      text: 'Onboarding Survey',
      icon: <QuizIcon />,
      path: '/onboarding-survey',
      permissionTab: 'onboarding_survey' as const,
    },
    {
      text: 'Media Library',
      icon: <StorageIcon />,
      path: '/media-library',
      permissionTab: 'onboarding_survey' as const, // Reuse onboarding permission for now
    },
    {
      text: 'Pipeline',
      icon: <PipelineIcon />,
      path: '/pipeline',
      permissionTab: 'settings' as const, // Reuse settings permission for pipeline
    },
    {
      text: 'CI/CD Setup',
      icon: <RocketLaunchIcon />,
      path: '/cicd-setup',
      permissionTab: 'settings' as const,
    },
    {
      text: 'Content',
      icon: <ContentIcon />,
      path: '/content',
      permissionTab: 'content' as const,
    },
    {
      text: 'Editorial CMS',
      icon: <ContentIcon />,
      path: '/content/cms',
      permissionTab: 'content' as const,
    },
    {
      text: 'Guides',
      icon: <GuidesIcon />,
      path: '/guides',
      permissionTab: 'content' as const, // Postgres guides reuse the content permission tab (Phase 0)
    },
    {
      text: 'Feature Flags',
      icon: <FeatureFlagsIcon />,
      path: '/feature-flags',
      permissionTab: 'feature_flags' as const,
    },
    {
      text: 'Staff',
      icon: <StaffIcon />,
      path: '/staff',
      permissionTab: 'staff' as const,
    },
    {
      text: 'User Management',
      icon: <UsersIcon />,
      path: '/users',
      permissionTab: 'user_management' as const,
    },
    {
      text: 'Concierges',
      icon: <ConciergeIcon />,
      path: '/concierges',
      permissionTab: 'onboarding_survey' as const, // Reuse permission for now
    },
  ];

  const quickLinks = getQuickLinks();

  const drawer = (
    <Box>
      <Toolbar sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DashboardIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle2" component="div" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              BY.AI Group
            </Typography>
            <Typography variant="subtitle2" component="div" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'primary.main' }}>
              Command Center
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => {
          // Use fine-grained permissions - check if user can view this tab
          const tabPerms = user?.effective_permissions?.[item.permissionTab];
          const hasAccess = user?.role === 'admin' || tabPerms?.view === true;

          if (!hasAccess) return null;

          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isNavItemSelected(location.pathname, item.path)}
                onClick={() => navigate(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Quick Links Section */}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ px: 2, py: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Quick Links
        </Typography>
        <Chip
          label={isProduction() ? 'PROD' : 'STAGING'}
          size="small"
          color={isProduction() ? 'error' : 'warning'}
          sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
        />
      </Box>
      <List dense>
        {quickLinks.map((link) => (
          <ListItem key={link.text} disablePadding>
            <ListItemButton
              component="a"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                py: 0.75,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                {link.icon}
              </ListItemIcon>
              <ListItemText
                primary={link.text}
                secondary={link.description}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.7rem' } }}
              />
              <OpenInNewIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 1 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {location.pathname === '/' && 'Dashboard'}
            {location.pathname === '/services' && 'Services'}
            {location.pathname === '/permissions' && 'Agent Permissions'}
            {location.pathname === '/service-registry' && 'Service Registry (Legacy)'}
            {location.pathname === '/service-discovery' && 'Service Discovery'}
            {location.pathname === '/background-search' && 'Background Search'}
            {location.pathname === '/audit-log' && 'Audit Log'}
            {location.pathname === '/match-review' && 'Match Review Queue'}
            {location.pathname === '/generic-terms' && 'Generic Terms Review'}
            {location.pathname === '/onboarding-survey' && 'Onboarding Survey Configuration'}
            {location.pathname === '/media-library' && 'Media Library'}
            {location.pathname === '/pipeline' && 'Pipeline Management'}
            {location.pathname === '/cicd-setup' && 'CI/CD Pipeline Setup'}
            {location.pathname === '/content' && 'Content Management'}
            {location.pathname.startsWith('/content/cms') && 'Editorial CMS'}
            {!location.pathname.startsWith('/content/cms') && location.pathname.startsWith('/content/') && 'Content Detail'}
            {location.pathname === '/guides' && 'Guides'}
            {location.pathname.startsWith('/guides/') && 'Guide Detail'}
            {location.pathname === '/feature-flags' && 'Feature Flags'}
            {location.pathname === '/staff' && 'Tripsby Staff'}
            {location.pathname === '/users' && 'User Management'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={user?.role || 'viewer'}
              color="primary"
              size="small"
              variant="outlined"
            />
            {user?.role !== 'admin' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<LockOpenIcon />}
                onClick={() => setRequestAccessOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Request Access
              </Button>
            )}
            <IconButton
              onClick={handleProfileMenuOpen}
              size="small"
              sx={{ ml: 2 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {user?.email[0].toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#FAFBFC',
              borderRight: '1px solid #E1E4E8',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {children}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/my-permissions');
        }}>
          <ListItemIcon>
            <SecurityIcon fontSize="small" />
          </ListItemIcon>
          My Permissions
        </MenuItem>
        {user?.role !== 'admin' && (
          <MenuItem onClick={() => {
            handleProfileMenuClose();
            setRequestAccessOpen(true);
          }}>
            <ListItemIcon>
              <LockOpenIcon fontSize="small" />
            </ListItemIcon>
            Request Access
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => signOut()}>
          Logout
        </MenuItem>
      </Menu>

      <RequestAccessDialog
        open={requestAccessOpen}
        onClose={() => setRequestAccessOpen(false)}
        currentRole={user?.role || 'viewer'}
        userEmail={user?.email || ''}
      />
    </Box>
  );
}
